// =============================================================================
// billing.ts (client) — Stripe subscription state + actions (v0.2.43)
// =============================================================================
//
// Reads two Firestore docs the Stripe Cloud Functions maintain:
//   config/app.billingEnabled  — the master switch (absent/false = billing off,
//                                app behaves free, no paywall anywhere)
//   billing/{uid}.active       — whether the signed-in user has a live sub
//
// And exposes the three actions the UI needs: start checkout, open the billing
// portal, and force-refresh the ID token (to pick up the `stripeRole` claim the
// webhook sets right after payment).
//
// Everything is a no-op when billing is off, so the app is unchanged until the
// owner flips config/app.billingEnabled to true.
// =============================================================================
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export interface BillingStatus {
  loading: boolean;
  billingEnabled: boolean;
  hasActiveSub: boolean;
}

export function useBillingStatus(): BillingStatus {
  const { user } = useAuth();
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [subLoaded, setSubLoaded] = useState(false);

  useEffect(() => {
    if (!db) {
      setCfgLoaded(true);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'config', 'app'),
      (snap) => {
        setBillingEnabled(snap.exists() && snap.data().billingEnabled === true);
        setCfgLoaded(true);
      },
      () => {
        setBillingEnabled(false);
        setCfgLoaded(true);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!db || !user) {
      setHasActiveSub(false);
      setSubLoaded(true);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'billing', user.uid),
      (snap) => {
        setHasActiveSub(snap.exists() && snap.data().active === true);
        setSubLoaded(true);
      },
      () => {
        setHasActiveSub(false);
        setSubLoaded(true);
      }
    );
    return () => unsub();
  }, [user]);

  return { loading: !cfgLoaded || !subLoaded, billingEnabled, hasActiveSub };
}

// Read a household's billing status (for the lapse banner). Returns the raw
// status string (null when absent = active/grandfathered) and the founder uid.
export function useHouseholdBilling(householdId: string | undefined) {
  const [billingStatus, setBillingStatus] = useState<string | null>(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !householdId) return;
    const unsub = onSnapshot(
      doc(db, 'households', householdId),
      (snap) => {
        const d = snap.data();
        setBillingStatus((d?.billingStatus as string) ?? null);
        setCreatedBy((d?.created_by as string) ?? null);
      },
      () => {}
    );
    return () => unsub();
  }, [householdId]);

  return { billingStatus, createdBy };
}

export async function startCheckout(): Promise<void> {
  if (!functions) throw new Error('Billing is not configured.');
  const fn = httpsCallable<Record<string, never>, { url?: string }>(functions, 'createCheckoutSession');
  const res = await fn({});
  const url = res.data?.url;
  if (!url) throw new Error('Could not start checkout. Please try again.');
  window.location.assign(url);
}

export async function openBillingPortal(): Promise<void> {
  if (!functions) throw new Error('Billing is not configured.');
  const fn = httpsCallable<Record<string, never>, { url?: string }>(functions, 'createPortalSession');
  const res = await fn({});
  const url = res.data?.url;
  if (!url) throw new Error('Could not open the billing portal. Please try again.');
  window.location.assign(url);
}

// Force-refresh the ID token so the `stripeRole` custom claim set by the webhook
// right after payment is visible to the security rules.
export async function refreshClaims(): Promise<void> {
  if (auth?.currentUser) {
    await auth.currentUser.getIdToken(true);
  }
}
